import { useContext, useMemo } from 'react';

import {
  ClockIcon,
  DefaultImageIcon,
  PrinterIcon,
  ShopIcon,
  Tooltip,
} from '@smartshell/ui-kit';
import { Column } from 'react-table';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { CommonItem, GoodItem } from './interfaces';
import s from './ItemsReport.module.scss';

export const useGetColumns = (): {
  COLUMNS_GOODS: Column<GoodItem>[];
  COLUMNS_TARIFFS: Column<CommonItem>[];
  COLUMNS_SERVICES: Column<CommonItem>[];
} => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const formatSum = (m: number | string | null, cur?: string): string =>
    `${Number(m).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${cur ?? ''}`;

  const COLUMNS_GOODS: Array<Column<GoodItem>> = useMemo(
    () => [
      {
        Header: (
          <div className={s.icon}>
            <ShopIcon color={'secondary'} />
          </div>
        ),
        id: 'img',
        accessor: ({ img }) => (!img ? 'empty' : img),
        Cell: ({ value }: { value: string }) => {
          return (
            <div className={s.column_img_block}>
              {value !== 'empty' ? (
                <Tooltip
                  className={s.column_img_tooltip}
                  contentClassName={s.column_img_content}
                  content={<img alt="food-icon" src={value} />}
                  position={'bottom-left'}
                >
                  <img alt="food-icon" className={s.column_img} src={value} />
                </Tooltip>
              ) : (
                <DefaultImageIcon color={'secondary'} />
              )}
            </div>
          );
        },
        width: 48,
        disableSortBy: true,
      },
      {
        Header: t('goods.top'),
        accessor: 'title',
        disableSortBy: true,
        width: 200,
      },
      {
        Header: t('amount'),
        accessor: 'quantity',
        Cell: ({ value }: { value: string }) => `${value} ${t('pcs')}`,
        disableSortBy: true,
        width: 115,
      },
      {
        Header: t('money.proceeds'),
        accessor: 'sum',
        Cell: ({ value }: { value: string }): string =>
          `${formatSum(value)} ${currency.sign}`,
        disableSortBy: true,
        width: 128,
      },
    ],
    [t]
  );

  const COLUMNS_TARIFFS: Array<Column<CommonItem>> = useMemo(
    () => [
      {
        Header: (
          <div className={s.icon}>
            <ClockIcon color={'secondary'} />
          </div>
        ),
        Cell: ({ value }: { value: string }) => {
          return (
            <div className={s.column_img_block}>
              {value !== 'empty' ? (
                <Tooltip
                  className={s.column_img_tooltip}
                  contentClassName={s.column_img_content}
                  content={<img alt="food-icon" src={value} />}
                  position={'bottom-left'}
                >
                  <img alt="food-icon" className={s.column_img} src={value} />
                </Tooltip>
              ) : (
                ''
              )}
            </div>
          );
        },
        id: 'img',
        accessor: ({ img }) => (!img ? 'empty' : img),
        width: 48,
        disableSortBy: true,
      },
      {
        Header: t('tariffs.top'),
        accessor: 'title',
        disableSortBy: true,
        width: 200,
      },
      {
        Header: t('amount'),
        accessor: 'quantity',
        Cell: ({ value }: { value: string }) => `${value} ${t('pcs')}`,
        disableSortBy: true,
        width: 115,
      },
      {
        Header: t('money.proceeds'),
        accessor: 'sum',
        Cell: ({ value }: { value: string }): string =>
          `${formatSum(value)} ${currency.sign}`,
        disableSortBy: true,
        width: 128,
      },
    ],
    [t]
  );

  const COLUMNS_SERVICES: Array<Column<CommonItem>> = useMemo(
    () => [
      {
        Header: (
          <div className={s.icon}>
            <PrinterIcon color={'secondary'} />
          </div>
        ),
        accessor: 'img',
        width: 48,
        disableSortBy: true,
      },
      {
        Header: t('services.top'),
        accessor: 'title',
        disableSortBy: true,
        width: 200,
      },
      {
        Header: t('amount'),
        accessor: 'quantity',
        Cell: ({ value }: { value: string }) => `${value} ${t('pcs')}`,

        disableSortBy: true,
        width: 115,
      },
      {
        Header: t('money.proceeds'),
        accessor: 'sum',
        Cell: ({ value }: { value: string }) =>
          `${formatSum(value)} ${currency.sign}`,
        disableSortBy: true,
        width: 128,
      },
    ],
    [t]
  );
  return {
    COLUMNS_GOODS,
    COLUMNS_TARIFFS,
    COLUMNS_SERVICES,
  };
};
